import React, { JSX, useState } from 'react';
import { Label } from 'reactstrap';
import { Controller, FieldValues } from 'react-hook-form';
import { useFormContext } from '../Form';
import { FilterOptionOption, Select } from '../../Components/Form/Select/Select';
import useConfig from '../../Hooks/useConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import useWindow from '../../Hooks/useWindow';
import { sanitize } from '@ibe/services';
import classNames from 'classnames';
import { getErrorMessage } from './utils';

interface Props<Option> {
  name: string;
  baseName?: string;
  label?: string;
  boldLabel?: boolean;
  ariaLabel?: string;
  errorsProp?: Record<string, string>;
  options: Option[];
  selectedOption?: (value: unknown) => Option | undefined;
  getLabel: (option: Option) => string;
  getValue: (option: Option) => string;
  showErrors?: boolean;
  getCurrentValue?: (option: Option) => unknown;
  placeholder?: string;
  isSearchable?: boolean;
  filterOption?: (option: FilterOptionOption<Option>, inputValue: string) => boolean;
  showErrorIcon?: boolean;
  noMenuPortal?: boolean;
  disabled?: boolean;
  validateOnBlurOnly?: boolean;
  showSuccessState?: boolean;
}

function FormSelect<Option extends object>(props: Props<Option>): JSX.Element | null {
  const {
    name,
    label,
    boldLabel,
    ariaLabel,
    options,
    errorsProp,
    baseName,
    selectedOption,
    getLabel,
    getValue,
    showErrors,
    getCurrentValue,
    placeholder,
    isSearchable,
    filterOption,
    showErrorIcon,
    noMenuPortal,
    disabled,
    validateOnBlurOnly,
    showSuccessState
  } = props;
  const window = useWindow();
  const config = useConfig();
  const { register, errors, control, formState, trigger } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const { _formValues } = control || {};

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasBeenBlurred, setHasBeenBlurred] = useState<boolean>(false);
  if (!register) return null;

  const formErrors = errorsProp || errors;
  const fieldName = baseName ?? name;
  const fieldError = formErrors?.[fieldName];
  const errorMessage = getErrorMessage(fieldError);

  const hasError = !!formErrors?.[fieldName];
  const isTouched = !!touchedFields?.[fieldName];
  const fieldValue = _formValues?.[fieldName];
  const hasValue = typeof fieldValue === 'string' ? fieldValue.length > 0 : !!fieldValue;

  const displayError = validateOnBlurOnly
    ? showErrors && hasError && (isSubmitted || isTouched || hasBeenBlurred) && !isFocused
    : showErrors && hasError;
  const displaySuccess = showSuccessState ? !hasError && hasValue && !isFocused : false;
  let resolvedMenuPortalTarget: HTMLElement | undefined = undefined;

  if (!noMenuPortal) {
    resolvedMenuPortalTarget = config.outsideElementsContainerId
      ? window?.document?.getElementById(config.outsideElementsContainerId) || undefined
      : window?.document?.body;
  }
  return (
    <div
      className={classNames('form-input form-select-' + name, {
        'input--error': displayError,
        'form-input--errors': displayError,
        'form-input--touched': isTouched,
        'form-input--dirty': dirtyFields?.[fieldName],
        'form-input--form-submitted': isSubmitted,
        'form-input--success': displaySuccess
      })}
      data-testid={'form-select-' + name}
    >
      {!!label && (
        <Label
          className={`${displayError ? 'form__error__top ' : ''}${
            boldLabel ? 'font-weight-bold' : ''
          }`}
          dangerouslySetInnerHTML={{ __html: sanitize(label, true) }}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }): JSX.Element => (
          <div className="position-relative">
            <Select
              options={options}
              /* eslint-disable-next-line */
              {...field}
              onMenuOpen={() => setIsFocused(true)}
              onMenuClose={() => setIsFocused(false)}
              onChange={(option): void =>
                field.onChange(getCurrentValue ? getCurrentValue(option) : option)
              }
              selectedOption={selectedOption ? selectedOption(field.value) : field.value}
              getLabel={getLabel}
              getValue={getValue}
              ariaLabel={ariaLabel ?? placeholder ?? ''}
              onBlur={async () => {
                field.onBlur();
                setIsFocused(false);
                setHasBeenBlurred(true);
                if (trigger) await trigger(fieldName);
              }}
              placeholder={placeholder}
              menuPortalTarget={resolvedMenuPortalTarget}
              isSearchable={isSearchable}
              filterOption={filterOption}
              isDisabled={disabled}
            />
            {displaySuccess && (
              <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />
            )}
          </div>
        )}
      />
      {displayError && errorMessage ? <div className="form__error">{errorMessage}</div> : null}
      {showErrorIcon && displayError && (
        <FontAwesomeIcon icon={faExclamationTriangle} className="form-input__error-icon" />
      )}
      {!formErrors?.[fieldName] &&
        (isTouched || isSubmitted) &&
        !!_formValues?.[fieldName] &&
        !showSuccessState && <span className="form-input__success" />}
    </div>
  );
}

export default FormSelect;
